/**
 * MCP Server Form Component
 * Add/Edit MCP server configuration
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { X, Plus, Trash2 } from 'lucide-react';
import type { MCPServer, MCPServerConfig } from '@/types/system';

interface MCPServerFormProps {
  server?: MCPServer;
  onSave: (serverId: string, config: MCPServerConfig) => void;
  onCancel: () => void;
}

type TransportType = 'stdio' | 'sse' | 'streamable-http';

export const MCPServerForm: React.FC<MCPServerFormProps> = ({ server, onSave, onCancel }) => {
  const [serverId, setServerId] = useState(server?.id || '');
  const [transport, setTransport] = useState<TransportType>(server?.config.transport || 'stdio');
  const [command, setCommand] = useState(server?.config.command || '');
  const [args, setArgs] = useState(server?.config.args?.join(' ') || '');
  const [url, setUrl] = useState(server?.config.url || '');
  const [description, setDescription] = useState(server?.config.description || '');
  const [status, setStatus] = useState<0 | 1>(server?.config.status ?? 1);
  const [envVars, setEnvVars] = useState<{ key: string; value: string }[]>(() => {
    if (!server?.config.env) return [];
    return Object.entries(server.config.env).map(([key, value]) => ({ key, value }));
  });

  // Helper to check if transport is URL-based
  const isUrlTransport = transport === 'sse' || transport === 'streamable-http';

  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { key: '', value: '' }]);
  };

  const handleRemoveEnvVar = (index: number) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
  };

  const handleEnvVarChange = (index: number, field: 'key' | 'value', value: string) => {
    const newEnvVars = [...envVars];
    newEnvVars[index][field] = value;
    setEnvVars(newEnvVars);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const config: MCPServerConfig = {
      transport,
      description,
      status,
    };

    if (transport === 'stdio') {
      if (!command.trim()) {
        alert('Command is required for stdio transport');
        return;
      }
      config.command = command.trim();
      if (args.trim()) {
        config.args = args.trim().split(/\s+/);
      }
    } else {
      // SSE or Streamable HTTP - both require URL
      if (!url.trim()) {
        alert(`URL is required for ${transport} transport`);
        return;
      }
      config.url = url.trim();
    }

    // Add environment variables
    const env: Record<string, string> = {};
    envVars.forEach(({ key, value }) => {
      if (key.trim()) {
        env[key.trim()] = value;
      }
    });
    if (Object.keys(env).length > 0) {
      config.env = env;
    }

    if (!serverId.trim()) {
      alert('Server ID is required');
      return;
    }

    onSave(serverId.trim(), config);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">
          {server ? 'Edit MCP Server' : 'Add MCP Server'}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Server ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Server ID *
          </label>
          <Input
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            placeholder="e.g., astro-tool"
            disabled={!!server}
            required
          />
          {server && (
            <p className="mt-1 text-xs text-gray-500">Server ID cannot be changed</p>
          )}
        </div>

        {/* Transport Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transport Type *
          </label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="stdio"
                checked={transport === 'stdio'}
                onChange={(e) => setTransport(e.target.value as TransportType)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">STDIO (local process)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="streamable-http"
                checked={transport === 'streamable-http'}
                onChange={(e) => setTransport(e.target.value as TransportType)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">Streamable HTTP</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="sse"
                checked={transport === 'sse'}
                onChange={(e) => setTransport(e.target.value as TransportType)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">SSE (legacy)</span>
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Streamable HTTP is recommended for remote MCP servers. SSE is for legacy servers.
          </p>
        </div>

        {/* STDIO Configuration */}
        {transport === 'stdio' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Command *
              </label>
              <Input
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                placeholder="e.g., uvx"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arguments
              </label>
              <Input
                value={args}
                onChange={(e) => setArgs(e.target.value)}
                placeholder="e.g., mcp-server-fetch"
              />
              <p className="mt-1 text-xs text-gray-500">Space-separated arguments</p>
            </div>
          </>
        )}

        {/* URL Configuration for SSE and Streamable HTTP */}
        {isUrlTransport && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL *
            </label>
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="http://localhost:8080/mcp"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              The MCP server endpoint URL (e.g., http://example.com/mcp)
            </p>
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this MCP server"
            rows={2}
          />
        </div>

        {/* Status */}
        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={status === 1}
              onChange={(e) => setStatus(e.target.checked ? 1 : 0)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Enable this server</span>
          </label>
        </div>

        {/* Environment Variables */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Environment Variables
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddEnvVar}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Variable
            </Button>
          </div>
          {envVars.length > 0 ? (
            <div className="space-y-2">
              {envVars.map((envVar, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={envVar.key}
                    onChange={(e) => handleEnvVarChange(index, 'key', e.target.value)}
                    placeholder="KEY"
                    className="flex-1"
                  />
                  <Input
                    value={envVar.value}
                    onChange={(e) => handleEnvVarChange(index, 'value', e.target.value)}
                    placeholder="value"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveEnvVar(index)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No environment variables</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {server ? 'Update Server' : 'Add Server'}
          </Button>
        </div>
      </form>
    </Card>
  );
};
